import { useState, useEffect } from 'react';
import { Box, Flex, IconButton, Button } from '@chakra-ui/react';
import { MdDelete, MdSearch, MdEdit } from 'react-icons/md';

import Page from '../components/common/Page';
import { ApiCommand } from '../lib/Api';
import useToastNotification from '../lib/hooks/useToastNotification';
import StrongTable from '../components/common/StrongTable';
import StrongPagination from '../components/common/StrongPagination';
import useApi from '../lib/hooks/useApi';
import urlConstants from '../lib/constants/url.constants';
import StrongInput from '../components/common/StrongInput';
import EditRestDayModal from '../components/restDays/EditRestDayModal';
import ThumbnailPreview from '../components/common/ThumbnailPreview';

const RESTDAY_COLUMNS = [
  {
    key: 'title',
    label: 'Title',
  },
  {
    key: 'description',
    label: 'Description',
  },
  {
    key: 'thumbnail',
    label: 'Thumbnail',
    render: (value: string) => <ThumbnailPreview imageUrl={value} />,
  },
];

const {
  createRestDay: createRestDayUrl,
  getRestDays: getRestDaysUrl,
  getVideosTitle: getVideosTitleUrl,
  editRestDay: editRestDayUrl,
  deleteRestDay: deleteRestDayUrl,
} = urlConstants.restDays

const RestDays = () => {

  const [restDays, setRestDays] = useState<RestDay[]>([]);

  const [videostitle, setVideosTitle] = useState<VideoTitle[]>([]);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [selectedRestDay, setSelectedRestDay] = useState<RestDay>();

  const showToast = useToastNotification();

  const { loading: isGetRestDaysLoading, sendRequest: getRestDays } =
    useApi<GetRestDaysResponse>();
  const { loading: isGetVideosTitleLoading, sendRequest: getVideostitle } =
    useApi<VideoTitle[]>();
  const { loading: isCreateRestDayLoading, sendRequest: createRestDay } =
    useApi<any>();
  const { loading: isEditRestDayLoading, sendRequest: editRestDay } =
    useApi<any>();
  const { loading: isDeleteRestDayLoading, sendRequest: deleteRestDay } =
    useApi<any>();

  const fetchRestDays = () => {
    getRestDays({
      callback: (data: GetRestDaysResponse | null, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        if (!data) return null;
        setRestDays(data.restDays);
        setTotalPages(data.pagination.totalPages);
        setTotalCount(data.pagination.totalCount);
      },
      command: ApiCommand.GET,
      url: getRestDaysUrl,
      options: {
        page: currentPage,
        limit: pageSize,
      },
    });
  };

  const fetchVideosTitles = () => {
    getVideostitle({
      callback: (data: VideoTitle[] | null, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        if (!data) return null;
        setVideosTitle(data);
      },
      command: ApiCommand.GET,
      url: getVideosTitleUrl,
    });
  };

  useEffect(() => {
    fetchRestDays();
    fetchVideosTitles();
  }, [currentPage, pageSize]);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const onSaveRestDay = (title: string, description: string, thumbnail: File | null , selectvideo: string) => {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('videoId', selectvideo);
 
    if (thumbnail) {
      formData.append('thumbnail', thumbnail);
    }
    if (selectedRestDay) {
      formData.append('restDayId', selectedRestDay._id || '');
      editRestDay({
        callback: (_data, error: string | null) => {
          if (error) {
            showToast({
              title: 'Failed',
              description: error,
              status: 'error',
            });
            return;
          }
          showToast({
            title: 'Success',
            //TODO create toast.constants.ts file and create variables for all toast description
            description: 'RestDay updated successfully',
            status: 'success',
          });
          setIsOpenModal(false);
          fetchRestDays();
        },
        command: ApiCommand.PUT,
        url: editRestDayUrl,
        options: formData,
      });
    } else {
      createRestDay({
        callback: (_data, error: string | null) => {
          if (error) {
            showToast({
              title: 'Failed',
              description: error,
              status: 'error',
            });
            return;
          }
          showToast({
            title: 'Success',
            description: 'RestDay created successfully',
            status: 'success',
          });
          setIsOpenModal(false);
          fetchRestDays();
        },
        command: ApiCommand.POST,
        url: createRestDayUrl,
        options: formData,
      });
    }
  };

  const onDeleteRestDay = (restDayId: string) => {
    deleteRestDay({
      callback: (_data, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        showToast({
          title: 'Success',
          description: 'RestDay deleted successfully',
          status: 'success',
        });
        fetchRestDays();
      },
      command: ApiCommand.DELETE,
      url: deleteRestDayUrl,
      options: {
        restDayId,
      },
    });
  };

  const onEditRestDay = (restDayId: string) => {
    setSelectedRestDay(
      restDays.find(({ _id }) => _id === restDayId),
    );
    setIsOpenModal(true);
  };

  return (
    <Page>
      <Flex
        justifyContent='space-between'
        alignItems='center'
        mb={6}
        gap={4}
        flexWrap='wrap'
      >
        <Box flex='1' maxW='400px'>
          <StrongInput
            placeholder='Search'
            rightIcon={<MdSearch color='gray.500' />}
          />
        </Box>
        <Button
          colorScheme='primary'
          px={6}
          py={4}
          borderRadius='md'
          fontWeight='bold'
          _hover={{ bg: 'primary.300' }}
          onClick={() => {
            setSelectedRestDay(undefined);
            setIsOpenModal(true);
          }}
        >
          Create RestDay
        </Button>
      </Flex>

      <Flex flexDirection='column' height='full' justifyContent='space-between'>
        <Box
          backgroundColor='white'
          borderRadius='lg'
          height='calc(100vh - 275px)'
        >
          <StrongTable
            columns={RESTDAY_COLUMNS}
            data={restDays}
            loading={isGetRestDaysLoading}
            actions={(row) => (
              <>
                <IconButton
                  aria-label='Edit RestDay'
                  icon={<MdEdit />}
                  variant='outline'
                  colorScheme='primary'
                  size='sm'
                  marginRight={4}
                  onClick={() => onEditRestDay(row._id)}
                />
                <IconButton
                  aria-label='Delete RestDay'
                  icon={<MdDelete />}
                  variant='outline'
                  colorScheme='red'
                  size='sm'
                  onClick={() => onDeleteRestDay(row._id)}
                />
              </>
            )}
          />
        </Box>

        <StrongPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </Flex>

      <EditRestDayModal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        onSubmit={onSaveRestDay}
        videostitle={videostitle}
        selectedRestDay={selectedRestDay}
      />
    </Page>
  );
};

export default RestDays;
